import React, { useCallback, useState } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import { Modal, Tooltip, Fieldset } from "components"

import Avatar from "react-avatar"
import { Popover } from "@headlessui/react"

import { Formik, Form, FieldArray, ErrorMessage } from "formik"

import BookingAdditionalProductForm from "components/Common/Forms/BookingAdditionalProduct"
import BookingAirplaneForm from "components/Common/Forms/BookingAirplane"
import BookingInsuranceForm from "components/Common/Forms/BookingInsurance"

import PassengersForm from "components/Common/Modals/Passengers"

import BookingCreateForm from "../create"

import SendEmailModal from "components/Common/Forms/SendEmailModal"

import cn from "classnames"

import formatDate from "utils/formatDate"
import formatMoney from "utils/formatMoney"
import groupBy from "lodash/groupBy"
import sortBy from "lodash/sortBy"

import { toast } from "react-hot-toast"

// ADDITONAL COMPONENTS, TODO: REFACTOR to /components
const DotsButton = ({ options }) => {
  return (
    <Popover className="relative">
      <Popover.Button>
        <button
          className="max-w-xs flex justify-center items-center text-sm rounded-full text-blue-500 focus:outline-none focus:shadow-solid"
          id="user-menu"
          aria-label="User menu"
          aria-haspopup="true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            // width="24px"
            // height="24px"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
            />
          </svg>
        </button>
      </Popover.Button>

      <Popover.Panel className="absolute z-50 right-0 px-4 mt-3  sm:px-0 lg:max-w-xs w-48">
        <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="relative z-50 flex flex-col bg-white">
            {options?.map((option, index) => (
              <button
                className="text-xs flex items-center space-x-4 hover:bg-blue-500 hover:text-white p-4"
                key={index}
                onClick={option.onClick}
              >
                <i className="block mr-4">{option?.icon}</i>
                {option.title}
              </button>
            ))}
          </div>
        </div>
      </Popover.Panel>
    </Popover>
  )
}

const BOOKING = gql`
  query Booking($id: ID!) {
    booking(id: $id) {
      id
      total
      timestamp
      status

      trip {
        id
        name
        url
        code
        startDate
        endDate
        photo {
          url
        }
        payment {
          price
        }

        location
        status

        settings {
          passengerFields
        }
      }

      user {
        id
        displayName
        name
        email
      }

      progress {
        category
        type
        amount
        paidAmount
        dueDate
        status
        executionPaymentDate
      }

      cart {
        id
        name
        quantity
        category
        price
      }

      insurance {
        value
        quote {
          insureds
          products
          total
        }
      }

      payments {
        id
        amount
        status
        method
        timestamp
        bookingId
        transactionId

        # date
      }

      passengers {
        id
        relationship
        firstName
        lastName
        email
        phone
        gender
        birthdate

        street
        city
        state
        region
        zip
        country

        passportNumber
        passportExparationDate

        weight
        height
        size

        emergencyContactPerson
        specialRequest

        roomateRequest
        smoker
        newsletter
      }
    }
  }
`

const UPDATE_BOOKING = gql`
  mutation UpdateBooking($input: UpdateBookingInput!) {
    updateBooking(input: $input) {
      id
    }
  }
`

const CANCEL_BOOKING = gql`
  mutation CancelBooking($id: ID!) {
    cancelBooking(id: $id) {
      id
      status
    }
  }
`

const ADD_ADDITIONAL_PRODUCT = gql`
  mutation AddBookingAdditionalProduct(
    $input: AddBookingAdditionalProductInput!
  ) {
    addBookingAdditionalProduct(input: $input) {
      id
    }
  }
`

const ADD_AIRPLANE = gql`
  mutation AddBookingAirplane($input: AddBookingAirplaneInput!) {
    addBookingAirplane(input: $input)
  }
`

const ADD_INSURANCE = gql`
  mutation AddBookingInsurance($input: AddBookingInsuranceInput!) {
    addBookingInsurance(input: $input) {
      id
    }
  }
`

const BookingPage = ({ id, user, onBack, onDelete }) => {
  const [addAdditionalProduct] = useMutation(ADD_ADDITIONAL_PRODUCT)
  const [addAirplane] = useMutation(ADD_AIRPLANE)
  const [addInsurance] = useMutation(ADD_INSURANCE)

  const [updateBooking] = useMutation(UPDATE_BOOKING)
  const [cancelBooking] = useMutation(CANCEL_BOOKING)
  const { data, loading, error, refetch } = useQuery(BOOKING, {
    variables: { id },
  })
  const booking = data?.booking || {}

  const handleUpdate = async (data) => {
    const input = {
      id,
      ...data,
    }

    await updateBooking({ variables: { input } })

    await toast.promise(refetch(), {
      loading: "Saving...",
      success: <b>Booking updated!</b>,
      error: <b>Could not save.</b>,
    })
  }

  const handleCancelBooking = async (id) => {
    if (window.confirm("Are you sure you want to cancel this booking?")) {
      try {
        await toast.promise(
          cancelBooking({
            variables: { id },
            update(cache, { data }) {
              const current = cache.readQuery({
                query: BOOKING,
                variables: { id },
              })

              cache.writeQuery({
                query: BOOKING,
                variables: { id },
                data: {
                  booking: {
                    ...current.booking,
                    ...data?.cancelBooking,
                  },
                },
              })
            },
          }),
          {
            loading: "Saving...",
            success: <b>Booking canceled!</b>,
            error: <b>Could not save.</b>,
          },
        )
      } catch (error) {}
    }
  }

  const handleAddAdditionalProduct = async (data) => {
    try {
      await toast.promise(
        Promise.all(
          data.map((input) =>
            addAdditionalProduct({
              variables: {
                input: {
                  bookingId: id,
                  tripId: booking?.trip?.id,
                  ...input,
                },
              },
            }),
          ),
        ).then(refetch),
        {
          loading: "Saving...",
          success: <b>Booking updated!</b>,
          error: <b>Could not save.</b>,
        },
      )
    } catch (error) {}
  }

  const handleAddAirplane = async (input) => {
    try {
      await addAirplane({
        variables: {
          input: {
            bookingId: id,
            tripId: booking?.trip?.id,
            ...input,
          },
        },
      })
      await toast.promise(refetch(), {
        loading: "Saving...",
        success: <b>Booking updated!</b>,
        error: <b>Could not save.</b>,
      })
    } catch (error) {}
  }

  const handleAddInsurance = async (input) => {
    // try {
    //   await addBookingInsurance({
    //     variables: { input: { bookingId: id, passengers } },
    //   })
    // } catch (error) {
    // }
  }

  const ENV = process.env.NODE_ENV
  const DEV_URL = `http://localhost:3000`
  const PROD_URL = `https://grouptravelres.com/`
  const BASE_URL = ENV === "development" ? DEV_URL : PROD_URL
  const URL = `${BASE_URL}/bookings/${booking?.id}`

  //Tabs
  const [selectedTab, setSelectedTab] = useState(0)

  const tabs = ["Details", "Insurance", "Passengers"]

  const numberOfLodgings = booking?.cart
    ? booking?.cart
        ?.filter((_) => _.category === "Lodging")
        ?.map((_) => _.quantity)
        ?.reduce((quantity, count) => count + Number(quantity), 0)
    : 0

  const containers = [
    <Details
      bookingId={booking?.id}
      userId={user?.id}
      startDate={booking?.trip?.startDate}
      endDate={booking?.trip?.endDate}
      //
      passengerFields={booking?.trip?.settings?.passengerFields}
      numberOfLodgings={numberOfLodgings}
      status={booking?.status}
      basePrice={booking?.trip?.payment?.price}
      cart={booking?.cart}
      passengers={booking?.passengers}
      payments={booking?.payments}
      progress={booking?.progress}
      total={booking?.total}
      refetch={refetch}
      onAddAdditionalProduct={handleAddAdditionalProduct}
      onAddAirplane={handleAddAirplane}
      onAddInsurance={handleAddInsurance}
      onEditBooking={() => setBookingModalOpen(true)}
    />,
    <Insurance data={booking?.insurance} />,
    <Passengers
      data={booking?.passengers}
      passengerFields={booking?.trip?.settings?.passengerFields}
      numberOfLodgings={numberOfLodgings}
      bookingId={booking?.id}
      tripId={booking?.trip?.id}
      userId={user?.id}
      refetch={refetch}
    />,
  ]

  const ActiveTab = () => containers[selectedTab]

  const [isEmailModalOpen, setEmailModalOpen] = useState(false)
  const [isBookingModalOpen, setBookingModalOpen] = useState(false)

  const EMAIL_BODY = `Dear Tanya,
Here is payment link for your booking for ${booking?.trip?.name}.
You can pay here:  
${URL}
  `

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg">
        {loading ? (
          <div className="py-16  flex flex-col justify-center items-center">
            <div>...Loading...</div>
          </div>
        ) : (
          <>
            <BoxHeader
              title={user?.displayName}
              subtitle={user?.email}
              status={booking?.status}
              url={URL}
              onBack={onBack}
              onCancel={() => handleCancelBooking(booking?.id)}
              onDelete={() => onDelete(booking?.id)}
              onSendEmail={() => setEmailModalOpen(true)}
            />

            <ul className="flex items-center border-b border-gray-300">
              {tabs?.map((tab, index) => (
                <li key={index} className="relative flex">
                  <button
                    type="button"
                    className={`group inline-flex items-center px-6 py-4 border-b-2 font-medium text-sm leading-5 cursor-pointer whitespace-no-wrap ${
                      selectedTab === index
                        ? "border-indigo-500 text-indigo-600 focus:outline-none focus:text-indigo-800 focus:border-indigo-700"
                        : "border-transparent text-gray-500 hover:text-gray-600 hover:border-gray-300 focus:text-gray-600 focus:border-gray-300"
                    }`}
                    onClick={() => setSelectedTab(index)}
                  >
                    {tab}
                  </button>
                </li>
              ))}
            </ul>

            <ActiveTab />
          </>
        )}
      </div>

      <EditBookingModal
        data={{
          cart: booking?.cart,
          tripId: booking?.trip?.id,
          userId: booking?.user?.id,
        }}
        isOpen={isBookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
        onSubmit={handleUpdate}
      />

      <SendEmailModal
        initialValues={{
          to: [user].map((_) => ({ label: _.displayName, value: { ..._ } })),
          subject: `Payment link for ${booking?.trip?.name}`,
          body: EMAIL_BODY,
        }}
        bcc={true}
        isOpen={isEmailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        // onSubmit={handleSendEmail}
      />
    </>
  )
}

export default BookingPage

const EditBookingModal = ({ isOpen, onClose, data, onSubmit }) => {
  return (
    <Modal title="Edit Booking" isOpen={isOpen} onClose={onClose}>
      <div className="max-w-screen-lg mx-auto ">
        <BookingCreateForm {...data} onClose={onClose} onSubmit={onSubmit} />
      </div>
    </Modal>
  )
}

const BoxHeader = ({
  title,
  subtitle,
  status,
  url,
  onBack,
  onCancel,
  onDelete,
  onEdit,
  onSendEmail,
}) => {
  return (
    <header className="p-6 border-b-2 border-gray-100 flex justify-between items-center">
      <div className="flex justify-center spaxe-x-4">
        <button onClick={onBack}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <div className="ml-4">
          <h3 className="text-xl leading-none font-medium text-gray-900">
            {title}
          </h3>

          <p className="text-base text-gray-700">{subtitle}</p>
        </div>
      </div>

      <nav className="flex items-start justify-end space-x-6 pt-2">
        <a
          href={url}
          target="_tab"
          className="text-blue-500 hover:text-blue-800"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>

        <button
          className="text-blue-500 hover:text-blue-800"
          onClick={onSendEmail}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              stroke-width="2"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </button>

        <DotsButton
          options={[
            // {
            //   icon: (
            //     <svg
            //       xmlns="http://www.w3.org/2000/svg"
            //       className="h-4 w-4"
            //       fill="none"
            //       viewBox="0 0 24 24"
            //       stroke="currentColor"
            //     >
            //       <path
            //         strokeLinecap="round"
            //         strokeLinejoin="round"
            //         strokeWidth={2}
            //         d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            //       />
            //     </svg>
            //   ),
            //   title: "Edit Booking",
            //   onClick: onEdit,
            // },

            {
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              ),
              title: "Cancel Booking",
              onClick: onCancel,
            },
            {
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              ),
              title: "Delete Booking",
              onClick: onDelete,
            },
          ]}
        />
      </nav>
    </header>
  )
}

//DETAILS
const Details = ({
  bookingId,
  userId,
  startDate,
  endDate,
  cart,
  passengers,
  payments,
  progress,
  basePrice,
  total,
  status,
  passengerFields,
  numberOfLodgings,
  refetch,
  onAddAdditionalProduct,
  onAddAirplane,
  onAddInsurance,
  onEditBooking,
}) => {
  return (
    <div className="flex justify-between">
      <BookingCart
        {...{
          bookingId,
          userId,
          startDate,
          endDate,
          cart,
          total,
          basePrice,
          passengers,
          passengerFields,
          numberOfLodgings,
          onAddAdditionalProduct,
          onAddAirplane,
          onAddInsurance,
          onEditBooking,
        }}
      />
      <BoookingPayment
        {...{ bookingId, userId, progress, payments, status, total, refetch }}
      />
    </div>
  )
}

const BookingCart = ({
  basePrice,
  cart,
  total,
  numberOfLodgings,
  passengerFields,
  onEditBooking,
  onAddAdditionalProduct,
  onAddAirplane,
  onAddInsurance,
}) => {
  const productsByCategory = groupBy(cart, (_) => _.category)

  const numberOfTravelers = cart
    ? cart
        ?.filter((_) => _.category === "Lodging")
        ?.map((_) => _.quantity)
        ?.reduce((quantity, count) => count + Number(quantity), 0)
    : 0

  const [state, setState] = useState(null)
  const closeModal = () => setState(null)
  const ACTIONS = [
    {
      // icon: (
      //   <svg
      //     xmlns="http://www.w3.org/2000/svg"
      //     className="h-4 w-4"
      //     fill="none"
      //     viewBox="0 0 24 24"
      //     stroke="currentColor"
      //   >
      //     <path
      //       strokeLinecap="round"
      //       strokeLinejoin="round"
      //       strokeWidth={2}
      //       d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      //     />
      //   </svg>
      // ),
      title: "Edit Booking",
      onClick: onEditBooking,
    },
    {
      title: "Add Additional Product",
      onClick: () => setState("ADDITIONAL_PRODUCTS"),
    },
    {
      title: "Add Air",
      onClick: () => setState("AIRPLANE"),
    },
    // {
    //   title: "Add Insurance",
    //   onClick: () => setState("INSURANCE"),
    // },
  ]

  return (
    <>
      <div className="w-6/12 flex flex-col justify-between pt-4">
        <header className="mt-4 px-6 flex justify-between">
          <legend className="w-full text-sm font-bold uppercase tracking-widest text-gray-500">
            Cart
          </legend>

          <DotsButton options={ACTIONS} />
        </header>

        <div className="mt-4 px-6 flex-1">
          {Object.keys(productsByCategory).map((category, i) => (
            <div>
              <p className="bg-purple-100 text-purple-800 my-2 p-2 inline-block text-xs">
                {category}
              </p>
              <ul key={i}>
                {productsByCategory[category].map((product, index) => (
                  <li key={index} className="flex justify-between">
                    <p>
                      {product.quantity} x {product.name}
                    </p>
                    <p>
                      {product.price === 0
                        ? "inc."
                        : formatMoney(product.price)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <footer className="mt-6 px-6  bg-gray-100">
          <div className="py-3 flex justify-between text-xs">
            <span>No. of passengers x Base Price</span>

            <span>
              {numberOfTravelers} x {formatMoney(basePrice ? basePrice : total)}
            </span>
          </div>
          <div className="py-3 flex justify-between">
            <span>Total:</span>
            <span>{formatMoney(total)}</span>
          </div>
        </footer>
      </div>

      <BookingAdditionalProductForm
        isOpen={state === "ADDITIONAL_PRODUCTS"}
        onClose={closeModal}
        onSubmit={onAddAdditionalProduct}
      />

      <BookingAirplaneForm
        isOpen={state === "AIRPLANE"}
        onClose={closeModal}
        onSubmit={onAddAirplane}
      />

      <BookingInsuranceForm
        passengerFields={passengerFields}
        numberOfLodgings={numberOfLodgings}
        isOpen={state === "INSURANCE"}
        onClose={closeModal}
        onSubmit={onAddInsurance}
      />
    </>
  )
}

const CREATE_PAYMENT = gql`
  mutation CreatePayment($input: CreatePaymentInput!) {
    createPayment(input: $input) {
      id
    }
  }
`
const UPDATE_PAYMENT = gql`
  mutation UpdatePayment($input: UpdatePaymentInput!) {
    updatePayment(input: $input) {
      id
    }
  }
`
const DELETE_PAYMENT = gql`
  mutation DeletePayment($id: ID!) {
    deletePayment(id: $id) {
      id
    }
  }
`
const VOID_TRANSACTION = gql`
  mutation VoidTransaction($id: ID!) {
    voidTransaction(id: $id) {
      id
    }
  }
`

const ADD_BOOKING_PROGRESS = gql`
  mutation AddBookingProgress($input: AddBookingProgressInput!) {
    addBookingProgress(input: $input)
  }
`

const ProgressBar = ({ title, progress }) => {
  const roundPercentage = (data) => {
    const arr = data.map((v) => v.amount)
    const total = arr?.reduce((a, b) => a + b, 0)
    let percentage = arr?.map((v) => Math.floor((Math.abs(v) / total) * 100))
    const totalPercentage = percentage?.reduce((a, b) => a + b, 0)
    // const minPercetange = Math.min(...percentage)

    let diffrence = 0
    percentage = percentage.map((percent, i) =>
      percent < 10 ? percent + (20 - percent) : percent,
    )

    if (totalPercentage !== 100) {
      const lowest = percentage.indexOf(Math.min(...percentage))
      const percToAdd = 100 - totalPercentage
      percentage = percentage.map((v, i) => (i === lowest ? v + percToAdd : v))
    }

    // const paidPerc =
    // const paidWidth = Math.floor(paidPerc)

    // booking.progress
    return data.map((v, i) => ({
      ...v,
      // width: `${Math.floor((Math.abs(v.amount) / total) * 100)}%`,
      width: `${percentage[i]}%`,
      // width: Math.floor(
      //   Math.abs(Math.floor(Math.abs(v.paidAmount / v.amount) * 100)),
      // ),
    }))
  }

  const bars = (data) => roundPercentage(data)

  return (
    <div className="px-6 my-3">
      <p className="text-xs text-gray-400 uppercase font-bold">{title}</p>
      <div className="bg-gray-100 w-full h-10 flex">
        {bars(progress)?.map((part, index) => (
          <>
            <div
              data-multiline
              data-for="tooltip"
              data-tip={JSON.stringify(part)}
              key={index}
              className={cn(`border-r-2`, {
                [`bg-green-500 `]: part.status === "PAID",
                [`bg-yellow-500 `]:
                  part.status === "NOT_PAID" || "PARTIAL_PAID",
                [`bg-red-500 `]: part.status === "LATE" || "CANCELED_BY_SYSTEM",
                [`bg-blue-500 `]: part.status === "PENDING",
              })}
              style={{ width: part.width }}
            >
              {part.status === "PARTIAL_PAID" && (
                <div
                  className="bg-green-500 h-full"
                  style={{
                    width: `${Math.floor(
                      (Math.abs(part.paidAmount) / part.amount) * 100,
                    )}%`,
                  }}
                />
              )}
            </div>
          </>
        ))}

        <Tooltip
          id="tooltip"
          place="top"
          type="dark"
          effect="solid"
          getContent={(_part) => {
            if (!_part) return null
            const part = JSON.parse(_part)

            return (
              <div>
                {/* <p>{part.id}</p> */}
                <p>Type: {part.type}</p>
                <p>Status: {part.status}</p>
                <p>Due date: {formatDate(part.dueDate, "dd. MMM yyyy")}</p>
                <p>Paid: {formatMoney(part.paidAmount)}</p>
                <p>Total: {formatMoney(part.amount)}</p>
              </div>
            )
          }}
        />
      </div>
    </div>
  )
}

const BoookingPayment = ({
  payments,
  progress,
  status,
  total,

  bookingId,
  userId,

  refetch,
}) => {
  const [createPayment] = useMutation(CREATE_PAYMENT)
  const [updatePayment] = useMutation(UPDATE_PAYMENT)
  const [deletePayment] = useMutation(DELETE_PAYMENT)
  const [voidTransaction] = useMutation(VOID_TRANSACTION)

  const [addBookingProgress] = useMutation(ADD_BOOKING_PROGRESS)

  const handleCreatePayment = async (data) => {
    try {
      const input = {
        ...data,
        bookingId,
        userId,
      }

      await toast.promise(createPayment({ variables: { input } }), {
        loading: "Saving...",
        success: <b>Payment created!</b>,
        error: <b>Could not save.</b>,
      })

      return await refetch()
    } catch (error) {
      console.error(error)
    }
  }

  const handleUpdatePayment = async (data) => {
    try {
      const input = {
        ...data,
        status: "PAID",
      }

      await toast.promise(updatePayment({ variables: { input } }), {
        loading: "Saving...",
        success: <b>Payment updated!</b>,
        error: <b>Could not save.</b>,
      })

      return await refetch()
    } catch (error) {
      console.error(error)
    }
  }

  const handleDeletePayment = async (id) => {
    if (
      window.confirm(`Are you sure you want to delete this payment? (${id})`)
    ) {
      try {
        await toast.promise(deletePayment({ variables: { id } }), {
          loading: "Saving...",
          success: <b>Payment deleted!</b>,
          error: <b>Could not save.</b>,
        })

        return await refetch()
      } catch (error) {
        console.error(error)
      }
    }
  }

  const handleVoidTransaction = async (id) => {
    if (window.confirm(`Are you sure you want to void this payment? (${id})`)) {
      try {
        await toast.promise(voidTransaction({ variables: { id } }), {
          loading: "Saving...",
          success: <b>Payment voided!</b>,
          error: <b>Could not save.</b>,
        })

        return await refetch()
      } catch (error) {
        console.error(error)
      }
    }
  }

  //
  const handleAddProgress = async (data) => {
    try {
      const input = {
        bookingId,
        ...data,
      }

      await toast.promise(addBookingProgress({ variables: { input } }), {
        loading: "Saving...",
        success: <b>Payment updated!</b>,
        error: <b>Could not save.</b>,
      })

      return await refetch()
    } catch (error) {
      console.error(error)
    }
  }

  const paid = progress.reduce((a, b) => a + b.paidAmount, 0)
  const owed = total - paid

  const regularProgress = progress.filter((_) => _.category !== "AIR")
  const airProgress = progress.filter((_) => _.category === "AIR")

  const [selectedPayment, setSelectedPayment] = useState(null)

  const [state, transition] = useState(null)
  const close = () => transition(null)

  return (
    <>
      <div className="w-6/12 flex flex-col justify-between pt-4">
        <header className="mt-4 px-6 flex justify-between">
          <legend className="w-full text-sm font-bold uppercase tracking-widest text-gray-400">
            Payment
          </legend>

          <DotsButton
            options={[
              {
                title: "Add Payment",
                onClick: () => transition("ADD_PAYMENT"),
              },
              {
                title: "Add Payment Assigment",
                onClick: () => transition("ADD_PAYMENT_ASSIGMENT"),
              },
            ]}
          />
        </header>

        {/* PROGRESS */}
        <ProgressBar title="Regular" progress={regularProgress} />

        {airProgress && airProgress.length > 0 && (
          <ProgressBar title="Air" progress={airProgress} />
        )}

        {/* PAYMENTS */}
        <div className="mt-4 px-6 flex-1">
          <ul className="w-full ">
            {sortBy(payments, "timestamp", ["desc"]).map((payment, index) => (
              <li
                key={index}
                className="flex justify-between items-center my-3"
              >
                <div className="pr-4">
                  {/* {payment.method === "CREDIT_CARD" && (
                    <svg width="32px" height="27px" viewBox="0 0 16.933 21.167">
                      <path
                        fill="#000"
                        stroke="none"
                        d="m 2.4998482,3.0468422 c -0.798891,0 -1.451075,0.650117 -1.451075,1.4490073 v 1.1761554 1.5523602 5.2131179 c 0,0.79889 0.652184,1.449008 1.451075,1.449008 H 14.433485 c 0.798891,0 1.451075,-0.650118 1.451075,-1.449008 V 7.2243651 5.6720049 4.4958495 c 0,-0.7988903 -0.652184,-1.4490073 -1.451075,-1.4490073 z m 0,0.5291667 H 14.433485 c 0.51489,0 0.921909,0.4049556 0.921909,0.9198406 V 5.4063881 H 1.5779392 V 4.4958495 c 0,-0.514885 0.407017,-0.9198406 0.921909,-0.9198406 z M 1.5779392,5.9355548 H 15.355394 V 6.958748 H 1.5779392 Z m 0,1.5528766 H 15.355394 v 4.9490516 c 0,0.514886 -0.407019,0.919841 -0.921909,0.919841 H 2.4998482 c -0.514892,0 -0.921909,-0.404955 -0.921909,-0.919841 z m 9.0842018,1.6350427 c -0.399971,4e-5 -0.7837838,0.159014 -1.0666048,0.441833 -0.282818,0.28282 -0.441793,0.6666339 -0.441833,1.0666019 4e-5,0.399968 0.159015,0.783782 0.441833,1.066602 0.282821,0.28282 0.6666338,0.44231 1.0666048,0.44235 0.320667,-2.08e-4 0.629777,-0.104181 0.885732,-0.290422 0.256342,0.186589 0.566312,0.290422 0.887285,0.290422 0.400019,0 0.783741,-0.158976 1.066602,-0.441834 0.28285,-0.282857 0.441833,-0.667098 0.441833,-1.067118 0,-0.400021 -0.158983,-0.7837449 -0.441833,-1.0666019 -0.282861,-0.282857 -0.666583,-0.441833 -1.066602,-0.441833 -0.321032,0 -0.630914,0.103768 -0.887285,0.290421 -0.255955,-0.186241 -0.565065,-0.290213 -0.885732,-0.290421 z m -7.4005838,0.402042 a 0.26464871,0.26464871 0 1 0 0,0.5291669 h 3.33003 a 0.26464871,0.26464871 0 1 0 0,-0.5291669 z m 7.4005838,0.127124 c 0.183798,1.38e-4 0.361693,0.05202 0.515212,0.147278 -0.03818,0.05771 -0.07147,0.118204 -0.101285,0.180351 -0.0035,0.0074 -0.0069,0.01479 -0.01034,0.02222 -0.0573,0.124597 -0.09715,0.25645 -0.118856,0.392741 -0.0016,0.01018 -0.0032,0.02026 -0.0047,0.03049 -0.0094,0.06813 -0.01602,0.136638 -0.01602,0.206189 0,0.06955 0.0066,0.138576 0.01602,0.206706 0.0014,0.01022 0.003,0.02031 0.0047,0.03049 0.02171,0.13629 0.06156,0.268144 0.118856,0.392741 0.0034,0.0074 0.0068,0.01484 0.01034,0.02222 0.02971,0.06193 0.06274,0.122303 0.100769,0.179834 -0.153432,0.09511 -0.331052,0.147139 -0.514696,0.147278 -0.259771,-5.4e-5 -0.508776,-0.103118 -0.6924648,-0.286805 -0.1837,-0.183699 -0.286766,-0.432676 -0.286806,-0.692464 4e-5,-0.259788 0.103106,-0.508249 0.286806,-0.6919469 0.1836888,-0.183687 0.4326938,-0.287268 0.6924648,-0.287321 z m 1.773017,0 c 0.25984,0 0.508733,0.103069 0.692463,0.286804 0.18374,0.1837359 0.286805,0.4326239 0.286805,0.6924649 0,0.259841 -0.103065,0.508729 -0.286805,0.692464 -0.18373,0.183736 -0.432623,0.287321 -0.692463,0.287321 -0.259842,0 -0.508735,-0.103585 -0.692464,-0.287321 -0.0017,-0.0017 -0.003,-0.0035 -0.0047,-0.0052 a 0.26468332,0.26468332 0 0 0 -5.16e-4,-10e-4 c -0.18012,-0.183055 -0.281416,-0.429458 -0.281635,-0.686263 2.19e-4,-0.256805 0.101526,-0.502692 0.281635,-0.6857469 a 0.26468332,0.26468332 0 0 0 0.0083,-0.0093 c 0.18346,-0.181932 0.430901,-0.284223 0.68938,-0.284223 z M 3.2362362,10.956954 a 0.26495306,0.26495306 0 0 0 0.02532,0.529166 h 1.423686 a 0.2646485,0.2646485 0 1 0 0,-0.529166 h -1.423686 a 0.26460979,0.26460979 0 0 0 -0.02532,0 z"
                      />
                    </svg>
                  )} */}

                  {payment.method === "CASH" && (
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="32" height="32" fill="white" />
                      <path
                        d="M20.9966 19.8195C20.7879 19.688 20.5616 19.5865 20.3246 19.5181C20.1001 19.4581 19.8899 19.3539 19.7064 19.2114C19.6439 19.1548 19.5946 19.0851 19.5621 19.0073C19.5296 18.9294 19.5147 18.8454 19.5185 18.7611C19.5134 18.6738 19.5265 18.5864 19.5572 18.5044C19.5878 18.4225 19.6352 18.3479 19.6963 18.2853C19.8387 18.1635 20.0233 18.1025 20.2102 18.1153C20.3076 18.1116 20.4045 18.1305 20.4933 18.1707C20.5821 18.2108 20.6604 18.2711 20.7219 18.3466C20.8557 18.5255 20.9225 18.7457 20.9107 18.9687H21.4303C21.4486 18.6494 21.3557 18.3336 21.1675 18.075C20.9825 17.8524 20.7169 17.7121 20.4288 17.6847V17.0762H20.0083V17.6821C19.7273 17.7033 19.4637 17.8263 19.267 18.0282C19.0835 18.2318 18.9869 18.499 18.9976 18.7729C18.9928 18.9074 19.0147 19.0415 19.0619 19.1675C19.1091 19.2935 19.1808 19.4089 19.2727 19.5072C19.5201 19.7355 19.8217 19.8969 20.1489 19.9764C20.3734 20.0369 20.5824 20.1444 20.7622 20.2918C20.8211 20.3464 20.8678 20.4128 20.8992 20.4867C20.9306 20.5607 20.946 20.6404 20.9445 20.7207C20.9485 20.811 20.9314 20.901 20.8946 20.9836C20.8578 21.0662 20.8023 21.1391 20.7324 21.1965C20.5653 21.3203 20.36 21.3815 20.1524 21.3695C20.046 21.3762 19.9393 21.3612 19.8389 21.3255C19.7385 21.2899 19.6463 21.2342 19.568 21.1618C19.4956 21.0821 19.4401 20.9886 19.4047 20.8869C19.3692 20.7852 19.3546 20.6774 19.3616 20.57H18.8381C18.825 20.7259 18.8442 20.8828 18.8944 21.0309C18.9447 21.179 19.0249 21.3152 19.1301 21.431C19.2352 21.5468 19.3631 21.6397 19.5058 21.7039C19.6484 21.7681 19.8028 21.8022 19.9592 21.8041V22.3333H20.3767V21.805C20.6757 21.7896 20.96 21.6701 21.1802 21.4672C21.2768 21.3679 21.352 21.25 21.4013 21.1205C21.4506 20.9911 21.4728 20.8529 21.4667 20.7146C21.4708 20.5372 21.4316 20.3616 21.3523 20.2029C21.2673 20.0476 21.1451 19.9159 20.9966 19.8195Z"
                        fill="black"
                      />
                      <path
                        d="M4.63373 10.0667H9.63809C9.75428 10.0667 9.86572 10.0205 9.94787 9.93834C10.03 9.85618 10.0762 9.74475 10.0762 9.62856C10.0762 9.51237 10.03 9.40094 9.94787 9.31878C9.86572 9.23662 9.75428 9.19047 9.63809 9.19047H4.23463C4.11844 9.19047 4.00701 9.23662 3.92485 9.31878C3.84269 9.40094 3.79653 9.51237 3.79653 9.62856C3.82069 9.96443 3.71172 10.2963 3.49317 10.5525C3.27462 10.8087 2.96405 10.9686 2.62857 10.9976C2.51238 10.9976 2.40095 11.0438 2.31879 11.1259C2.23663 11.2081 2.19048 11.3195 2.19048 11.4357V17.4595C2.19048 17.5757 2.23663 17.6871 2.31879 17.7693C2.40095 17.8515 2.51238 17.8976 2.62857 17.8976C2.96405 17.9267 3.27462 18.0866 3.49317 18.3427C3.71172 18.5989 3.82069 18.9308 3.79653 19.2667C3.79653 19.3828 3.84269 19.4943 3.92485 19.5764C4.00701 19.6586 4.11844 19.7048 4.23463 19.7048H9.37524C9.49143 19.7048 9.60286 19.6586 9.68502 19.5764C9.76718 19.4943 9.81333 19.3828 9.81333 19.2667C9.81333 19.1505 9.76718 19.039 9.68502 18.9569C9.60286 18.8747 9.49143 18.8286 9.37524 18.8286H4.63373C4.56962 18.4161 4.38757 18.0309 4.10953 17.7195C3.83148 17.4081 3.46931 17.1838 3.06667 17.0736V11.8217C3.46931 11.7114 3.83148 11.4871 4.10953 11.1757C4.38757 10.8643 4.56962 10.4792 4.63373 10.0667Z"
                        fill="black"
                      />
                      <path
                        d="M24.9714 7H1.31429C0.965715 7 0.631422 7.13847 0.384945 7.38495C0.138469 7.63142 0 7.96572 0 8.31429V20.581C0 20.9295 0.138469 21.2638 0.384945 21.5103C0.631422 21.7568 0.965715 21.8952 1.31429 21.8952H12.3705C12.4867 21.8952 12.5981 21.8491 12.6803 21.7669C12.7624 21.6848 12.8086 21.5733 12.8086 21.4571C12.8086 21.341 12.7624 21.2295 12.6803 21.1474C12.5981 21.0652 12.4867 21.019 12.3705 21.019H1.31429C1.1981 21.019 1.08666 20.9729 1.00451 20.8907C0.922347 20.8086 0.87619 20.6971 0.87619 20.581V8.31429C0.87619 8.1981 0.922347 8.08666 1.00451 8.00451C1.08666 7.92235 1.1981 7.87619 1.31429 7.87619H24.9714C25.0876 7.87619 25.199 7.92235 25.2812 8.00451C25.3634 8.08666 25.4095 8.1981 25.4095 8.31429V14.0792C25.4095 14.1954 25.4557 14.3068 25.5378 14.389C25.62 14.4711 25.7314 14.5173 25.8476 14.5173C25.9638 14.5173 26.0752 14.4711 26.1574 14.389C26.2396 14.3068 26.2857 14.1954 26.2857 14.0792V8.31429C26.2857 7.96572 26.1472 7.63142 25.9008 7.38495C25.6543 7.13847 25.32 7 24.9714 7V7Z"
                        fill="black"
                      />
                      <path
                        d="M20.1524 14.0095C19.026 14.0095 17.9249 14.3435 16.9883 14.9693C16.0517 15.5951 15.3217 16.4846 14.8907 17.5253C14.4596 18.566 14.3468 19.7111 14.5666 20.8158C14.7863 21.9206 15.3287 22.9354 16.1252 23.7319C16.9217 24.5284 17.9365 25.0708 19.0413 25.2906C20.1461 25.5103 21.2912 25.3975 22.3319 24.9665C23.3725 24.5354 24.262 23.8054 24.8878 22.8689C25.5136 21.9323 25.8476 20.8312 25.8476 19.7048C25.8459 18.1948 25.2453 16.7472 24.1776 15.6795C23.1099 14.6119 21.6623 14.0113 20.1524 14.0095V14.0095ZM20.1524 24.5238C19.1993 24.5238 18.2675 24.2412 17.4751 23.7116C16.6826 23.1821 16.0649 22.4295 15.7002 21.5489C15.3354 20.6684 15.24 19.6994 15.4259 18.7646C15.6119 17.8298 16.0708 16.9711 16.7448 16.2972C17.4188 15.6232 18.2774 15.1643 19.2122 14.9783C20.147 14.7924 21.116 14.8878 21.9965 15.2525C22.8771 15.6173 23.6297 16.235 24.1593 17.0274C24.6888 17.8199 24.9714 18.7516 24.9714 19.7048C24.9699 20.9824 24.4617 22.2073 23.5583 23.1107C22.6549 24.0141 21.43 24.5223 20.1524 24.5238Z"
                        fill="black"
                      />
                      <path
                        d="M23.6571 13.0321C23.7733 13.0321 23.8848 12.986 23.9669 12.9038C24.0491 12.8217 24.0952 12.7102 24.0952 12.594V11.4357C24.0952 11.3195 24.0491 11.2081 23.9669 11.1259C23.8848 11.0438 23.7733 10.9976 23.6571 10.9976C23.3152 10.9755 22.9957 10.8193 22.7683 10.5629C22.5409 10.3065 22.4239 9.97074 22.4427 9.62856C22.4427 9.51237 22.3966 9.40094 22.3144 9.31878C22.2323 9.23662 22.1208 9.19047 22.0046 9.19047H16.6476C16.5314 9.19047 16.42 9.23662 16.3378 9.31878C16.2557 9.40094 16.2095 9.51237 16.2095 9.62856C16.2095 9.74475 16.2557 9.85618 16.3378 9.93834C16.42 10.0205 16.5314 10.0667 16.6476 10.0667H21.606C21.6753 10.4845 21.8648 10.8732 22.1512 11.1853C22.4376 11.4974 22.8086 11.7194 23.219 11.8243V12.594C23.219 12.7102 23.2652 12.8217 23.3474 12.9038C23.4295 12.986 23.5409 13.0321 23.6571 13.0321Z"
                        fill="black"
                      />
                    </svg>
                  )}

                  {payment.method === "CREDIT_CARD" && (
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="32" height="32" fill="white" />
                      <path
                        d="M2.44523 7C1.099 7 0 8.09552 0 9.44174V11.4237V14.0396V22.8243C0 24.1705 1.099 25.2661 2.44523 25.2661H22.5548C23.901 25.2661 25 24.1705 25 22.8243V14.0396V11.4237V9.44174C25 8.09552 23.901 7 22.5548 7H2.44523ZM2.44523 7.89171H22.5548C23.4224 7.89171 24.1083 8.5741 24.1083 9.44174V10.9761H0.891705V9.44174C0.891705 8.5741 1.57758 7.89171 2.44523 7.89171V7.89171ZM0.891705 11.8678H24.1083V13.592H0.891705V11.8678ZM0.891705 14.4846H24.1083V22.8243C24.1083 23.6919 23.4224 24.3743 22.5548 24.3743H2.44523C1.57758 24.3743 0.891705 23.6919 0.891705 22.8243V14.4846ZM16.1996 17.2398C15.5256 17.2399 14.8789 17.5078 14.4023 17.9844C13.9257 18.4609 13.6578 19.1077 13.6577 19.7817C13.6578 20.4557 13.9257 21.1025 14.4023 21.5791C14.8789 22.0556 15.5256 22.3244 16.1996 22.3245C16.74 22.3241 17.2609 22.1489 17.6922 21.8351C18.1242 22.1495 18.6465 22.3245 19.1874 22.3245C19.8614 22.3245 20.5081 22.0566 20.9847 21.5799C21.4613 21.1033 21.7292 20.4558 21.7292 19.7817C21.7292 19.1076 21.4613 18.461 20.9847 17.9844C20.5081 17.5077 19.8614 17.2398 19.1874 17.2398C18.6464 17.2398 18.1242 17.4147 17.6922 17.7292C17.2609 17.4154 16.74 17.2402 16.1996 17.2398V17.2398ZM3.72879 17.9173C3.66941 17.916 3.61036 17.9265 3.55511 17.9484C3.49987 17.9702 3.44953 18.0028 3.40707 18.0443C3.3646 18.0859 3.33086 18.1355 3.30783 18.1902C3.28479 18.245 3.27293 18.3038 3.27293 18.3632C3.27293 18.4226 3.28479 18.4814 3.30783 18.5361C3.33086 18.5909 3.3646 18.6405 3.40707 18.682C3.44953 18.7235 3.49987 18.7562 3.55511 18.778C3.61036 18.7998 3.66941 18.8103 3.72879 18.809H9.34028C9.39966 18.8103 9.45871 18.7998 9.51396 18.778C9.5692 18.7562 9.61954 18.7235 9.662 18.682C9.70447 18.6405 9.73821 18.5909 9.76124 18.5361C9.78428 18.4814 9.79614 18.4226 9.79614 18.3632C9.79614 18.3038 9.78428 18.245 9.76124 18.1902C9.73821 18.1355 9.70447 18.0859 9.662 18.0443C9.61954 18.0028 9.5692 17.9702 9.51396 17.9484C9.45871 17.9265 9.39966 17.916 9.34028 17.9173H3.72879ZM16.1996 18.1315C16.5093 18.1318 16.8091 18.2192 17.0678 18.3797C17.0035 18.477 16.9474 18.5789 16.8971 18.6836C16.8912 18.6961 16.8855 18.7085 16.8797 18.7211C16.7832 18.931 16.716 19.1532 16.6794 19.3829C16.6767 19.4 16.674 19.417 16.6715 19.4343C16.6557 19.5491 16.6445 19.6645 16.6445 19.7817C16.6445 19.8989 16.6556 20.0152 16.6715 20.13C16.6739 20.1473 16.6766 20.1643 16.6794 20.1814C16.716 20.4111 16.7832 20.6333 16.8797 20.8432C16.8854 20.8557 16.8912 20.8682 16.8971 20.8807C16.9472 20.985 17.0029 21.0868 17.0669 21.1837C16.8084 21.344 16.5091 21.4317 16.1996 21.4319C15.7619 21.4318 15.3423 21.2581 15.0327 20.9486C14.7232 20.639 14.5495 20.2195 14.5494 19.7817C14.5495 19.3439 14.7232 18.9253 15.0327 18.6157C15.3423 18.3062 15.7619 18.1316 16.1996 18.1315V18.1315ZM19.1874 18.1315C19.6252 18.1315 20.0446 18.3052 20.3542 18.6148C20.6639 18.9244 20.8375 19.3438 20.8375 19.7817C20.8375 20.2196 20.6639 20.639 20.3542 20.9486C20.0446 21.2582 19.6252 21.4328 19.1874 21.4328C18.7495 21.4328 18.3301 21.2582 18.0205 20.9486C18.0176 20.9457 18.0154 20.9427 18.0126 20.9398C18.0123 20.9393 18.012 20.9387 18.0117 20.9381C17.7082 20.6297 17.5375 20.2145 17.5371 19.7817C17.5375 19.349 17.7082 18.9346 18.0117 18.6261C18.0165 18.621 18.0211 18.6158 18.0257 18.6105C18.3348 18.3039 18.7518 18.1315 19.1874 18.1315V18.1315ZM3.68613 20.3294C3.56974 20.3376 3.4612 20.3911 3.38372 20.4783C3.30623 20.5655 3.26597 20.6796 3.27155 20.7962C3.27712 20.9127 3.32809 21.0224 3.41355 21.1018C3.49901 21.1813 3.61216 21.2241 3.72879 21.2212H6.12787C6.18725 21.2225 6.2463 21.2119 6.30155 21.1901C6.3568 21.1683 6.40714 21.1357 6.4496 21.0941C6.49207 21.0526 6.52581 21.003 6.54885 20.9483C6.57188 20.8935 6.58375 20.8347 6.58375 20.7753C6.58375 20.7159 6.57188 20.6571 6.54885 20.6023C6.52581 20.5476 6.49207 20.498 6.4496 20.4565C6.40714 20.4149 6.3568 20.3823 6.30155 20.3605C6.2463 20.3387 6.18725 20.3281 6.12787 20.3294H3.72879C3.71458 20.3288 3.70034 20.3288 3.68613 20.3294V20.3294Z"
                        fill="black"
                      />
                    </svg>
                  )}

                  {payment.method === "CHECK" && (
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="32" height="32" fill="white" />
                      <path
                        d="M17.2041 7.83443H2.27331C1.01981 7.83443 0 8.85424 0 10.1077V23.4729C0 24.7264 1.01981 25.7462 2.27331 25.7462H5.09538C5.35787 25.7462 5.57062 25.5334 5.57062 25.271C5.57062 25.0086 5.35787 24.7958 5.09538 24.7958H2.27331C1.54388 24.7958 0.9505 24.2024 0.9505 23.473V10.1077C0.9505 9.3783 1.54394 8.78493 2.27331 8.78493H17.2041C17.4666 8.78493 17.6794 8.57211 17.6794 8.30968C17.6794 8.04724 17.4667 7.83443 17.2041 7.83443Z"
                        fill="black"
                      />
                      <path
                        d="M29.7266 7.83443H28.9903C28.7278 7.83443 28.5151 8.04724 28.5151 8.30968C28.5151 8.57211 28.7278 8.78493 28.9903 8.78493H29.7266C30.4561 8.78493 31.0495 9.37836 31.0495 10.1077V23.4729C31.0495 24.2024 30.4561 24.7957 29.7266 24.7957H22.9388V16.4957C22.9388 16.2333 22.7261 16.0205 22.4636 16.0205C22.2011 16.0205 21.9883 16.2333 21.9883 16.4957V24.7957H6.67969C6.41719 24.7957 6.20444 25.0086 6.20444 25.271C6.20444 25.5334 6.41719 25.7462 6.67969 25.7462H29.7266C30.9801 25.7462 32 24.7264 32 23.4729V10.1077C32 8.85424 30.9801 7.83443 29.7266 7.83443Z"
                        fill="black"
                      />
                      <path
                        d="M27.8349 8.21755C28.0484 7.72618 28.0449 7.22474 27.8246 6.76749C27.3702 5.82443 26.1059 5.28693 25.4736 5.0698C25.4061 4.24568 24.904 3.53268 24.1214 3.19255C23.2174 2.79974 22.2048 3.0188 21.5412 3.75049C21.2199 4.1048 20.8955 4.49949 20.5771 4.92349C20.4196 5.13343 20.4619 5.4313 20.6718 5.58893C20.8816 5.74649 21.1796 5.70411 21.3372 5.49424C21.638 5.09361 21.9436 4.7218 22.2453 4.38905C22.7132 3.87311 23.3256 3.88299 23.7426 4.0643C24.1597 4.24561 24.5847 4.68655 24.5268 5.38061C24.5267 5.38243 24.5264 5.38436 24.5262 5.38618C24.5261 5.38824 24.5257 5.3903 24.5256 5.39236C24.3651 7.29299 23.7589 9.57199 22.8099 11.8563L18.6824 10.0624C19.2107 8.89811 19.7985 7.80386 20.4336 6.8048C20.5745 6.5833 20.509 6.28955 20.2875 6.14874C20.0661 6.00793 19.7722 6.07324 19.6315 6.29486C18.8917 7.45855 18.2142 8.74424 17.618 10.1162C17.618 10.1166 17.6177 10.1171 17.6174 10.1176C17.6172 10.1181 17.6171 10.1186 17.6169 10.1191C15.7466 14.4227 15.1086 18.624 15.987 20.717L15.7574 21.2454C15.6528 21.4862 15.7631 21.7661 16.0039 21.8707C16.0656 21.8976 16.1298 21.9103 16.193 21.9103C16.3765 21.9103 16.5513 21.8034 16.6291 21.6243L16.8586 21.0963C18.9884 20.3106 21.6253 16.9771 23.496 12.6728C23.496 12.6727 23.4961 12.6727 23.4961 12.6727C23.4961 12.6726 23.4961 12.6725 23.4961 12.6724C24.4925 10.3798 25.1616 8.05943 25.4099 6.06161C25.9755 6.28711 26.7282 6.68143 26.9685 7.18018C27.0693 7.38949 27.0677 7.5988 26.9634 7.83886C26.7764 8.2693 26.4311 8.75861 26.0654 9.27661C25.1407 10.5868 23.9915 12.2156 24.365 14.2943C24.3762 14.6263 24.4871 14.9579 24.7047 15.2406C25.0287 15.6618 25.5179 15.8827 26.0123 15.8827C26.3631 15.8827 26.7164 15.7716 27.0151 15.5416C27.3639 15.2732 27.5873 14.8851 27.6442 14.4487C27.7011 14.0124 27.5846 13.58 27.3162 13.2312C26.8399 12.6121 26.007 12.4259 25.325 12.7381C25.5531 11.6516 26.2262 10.6968 26.8419 9.82461C27.2363 9.26555 27.6089 8.73743 27.8349 8.21755ZM19.4348 17.7353C18.4947 18.9061 17.5655 19.7296 16.7717 20.1034C16.5034 19.268 16.4716 18.0268 16.6862 16.5407C16.9359 14.8119 17.4932 12.8822 18.3032 10.934L22.4309 12.7279C21.5591 14.6493 20.5286 16.3732 19.4348 17.7353ZM25.5852 13.6834C25.8899 13.449 26.3285 13.5061 26.5628 13.8107C26.7972 14.1154 26.7401 14.5539 26.4354 14.7882C26.1308 15.0226 25.6922 14.9654 25.4579 14.6608C25.2235 14.3563 25.2806 13.9178 25.5852 13.6834Z"
                        fill="black"
                      />
                      <path
                        d="M9.15662 14.3731H2.43981C2.17731 14.3731 1.96456 14.5859 1.96456 14.8483C1.96456 15.1107 2.17731 15.3236 2.43981 15.3236H9.15662C9.41912 15.3236 9.63188 15.1107 9.63188 14.8483C9.63188 14.5859 9.41912 14.3731 9.15662 14.3731Z"
                        fill="black"
                      />
                      <path
                        d="M13.719 16.6542H5.98256C5.72006 16.6542 5.50731 16.867 5.50731 17.1294C5.50731 17.3919 5.72006 17.6047 5.98256 17.6047H13.719C13.9815 17.6047 14.1943 17.3919 14.1943 17.1294C14.1943 16.867 13.9815 16.6542 13.719 16.6542Z"
                        fill="black"
                      />
                      <path
                        d="M4.39825 16.6542H2.43981C2.17731 16.6542 1.96456 16.867 1.96456 17.1294C1.96456 17.3919 2.17731 17.6047 2.43981 17.6047H4.39825C4.66075 17.6047 4.8735 17.3919 4.8735 17.1294C4.8735 16.867 4.66081 16.6542 4.39825 16.6542Z"
                        fill="black"
                      />
                      <path
                        d="M15.4491 22.5596C15.2634 22.3741 14.9626 22.3741 14.7769 22.5597C14.5274 22.8092 14.4034 22.8371 14.3824 22.8401C14.2169 22.7597 13.9856 22.0663 13.8741 21.7315C13.6821 21.1554 13.5007 20.6112 13.1679 20.2784C12.9476 20.0582 12.7093 19.9607 12.4589 19.9885C12.0329 20.0359 11.7999 20.4044 11.5533 20.7946C11.2329 21.3014 10.9735 21.66 10.5507 21.66C10.2882 21.66 10.0755 21.8728 10.0755 22.1352C10.0755 22.3977 10.2882 22.6105 10.5507 22.6105C11.5298 22.6105 12.0274 21.8234 12.3567 21.3025C12.4124 21.2143 12.4862 21.0975 12.5484 21.0127C12.7024 21.2219 12.8451 21.6499 12.9724 22.0321C13.2308 22.8071 13.4979 23.6085 14.1653 23.766C14.2385 23.7833 14.3102 23.7911 14.3802 23.7911C14.8897 23.791 15.3019 23.3791 15.4492 23.2317C15.6347 23.0461 15.6347 22.7451 15.4491 22.5596Z"
                        fill="black"
                      />
                      <path
                        d="M29.5606 22.2938H24.4279C24.1654 22.2938 23.9526 22.5066 23.9526 22.7691C23.9526 23.0315 24.1654 23.2443 24.4279 23.2443H29.5606C29.8231 23.2443 30.0358 23.0315 30.0358 22.7691C30.0358 22.5066 29.8231 22.2938 29.5606 22.2938Z"
                        fill="black"
                      />
                      <path
                        d="M26.8358 20.2027H24.4279C24.1654 20.2027 23.9526 20.4155 23.9526 20.678C23.9526 20.9404 24.1654 21.1532 24.4279 21.1532H26.8358C27.0983 21.1532 27.3111 20.9404 27.3111 20.678C27.3111 20.4155 27.0984 20.2027 26.8358 20.2027Z"
                        fill="black"
                      />
                    </svg>
                  )}
                </div>

                <div className="flex-1">
                  <h4>{formatMoney(payment.amount)}</h4>
                  <p className="text-xs text-gray-500">
                    {formatDate(payment.timestamp, "dd, MMMM yyyy HH:mm")}
                  </p>
                </div>

                <div>
                  <o
                    className={cn(
                      `px-2 inline-flex text-xs leading-5 font-semibold rounded-full`,
                      {
                        [`bg-green-100 text-green-800`]:
                          payment.status === "PAID" ||
                          payment.status === "FINISHED",
                        [`bg-yellow-100 text-yellow-800`]:
                          payment.status === "CREATED",
                        [`bg-red-100 text-red-800`]:
                          payment.status === "CANCELED" || "CANCELED_BY_SYSTEM",
                        [`bg-blue-100 text-blue-800`]:
                          payment.status === "IN_PROGRESS",
                      },
                    )}
                  >
                    {payment.status}
                  </o>
                </div>

                <div className="ml-4 h-6 w-6">
                  <DotsButton
                    options={[
                      // { title: "", onClick: openModal }

                      ...(payment.status === "PAID"
                        ? [
                            {
                              title: "Delete Payment",
                              onClick: () => handleDeletePayment(payment.id),
                            },
                            // {
                            //   title: "Void Payment",
                            //   onClick: () => handleVoidTransaction(payment.id),
                            // },
                          ]
                        : []),
                      ...// payment.method === "CHECK" &&
                      (payment.status !== "PAID"
                        ? [
                            {
                              title: "Confirm Check",
                              // onClick: () => handleUpdatePayment(payment),
                              onClick: () => {
                                const { transactionId, ...rest } = payment
                                setSelectedPayment(rest)
                                transition("ADD_PAYMENT")
                              },
                            },
                          ]
                        : []),
                    ]}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* FOOTER */}
        <footer className="mt-6 px-6  bg-gray-100">
          <div className="py-3 flex justify-between text-xs">
            <span>Status</span>

            <span
              className={cn(`rounderf-full px-1`, {
                [`bg-green-200 text-green-800`]: status === "PAID",
                // [`bg-yellow-500 `]:
                //   status === "I" || "PARTIAL_PAID",
                [`bg-red-200 text-red-800`]: status === "CANCELED",
                [`bg-blue-200 text-blue-800`]: status === "IN_PROGRESS",
              })}
            >
              {status?.replace("_", " ")}
            </span>
          </div>
          <div className="py-3 flex justify-between">
            <span>Paid / Owed:</span>
            <span>
              {formatMoney(paid)} / {formatMoney(owed)}
            </span>
          </div>
        </footer>
      </div>

      {state === "ADD_PAYMENT_ASSIGMENT"}

      <PaymentAssigmentFormModal
        isOpen={state === "ADD_PAYMENT_ASSIGMENT"}
        onClose={close}
        data={progress}
        onSubmit={handleAddProgress}
      />

      <PaymentFormModal
        isOpen={state === "ADD_PAYMENT"}
        onClose={close}
        data={selectedPayment}
        onCreate={handleCreatePayment}
        onUpdate={handleUpdatePayment}
      />
    </>
  )
}

const PaymentFormModal = ({ isOpen, data, onClose, onCreate, onUpdate }) => {
  const PAYMENT_METHODS = [
    { value: "CREDIT_CARD", label: "Credit card" },
    { value: "CHECK", label: "Check" },
    { value: "CASH", label: "Cash/Credit" },
    // { value: "CREDIT", label: "Credit" },
  ]

  const isCreating = !data

  const formikProps = {
    initialValues: {
      amount: null,
      timestamp: new Date().toISOString(),
      method: null,
      status: "PAID",
      ...(data
        ? {
            ...data,
            timestamp: formatDate(data.timestamp, "yyyy-MM-dd"),
            method: { ...PAYMENT_METHODS.find((_) => _.value === data.method) },
          }
        : {}),
    },
    validateOnBlur: false,
    validateOnChange: false,
    // validationSchema: createTripSchema,
    onSubmit: useCallback(async (values, actions) => {
      try {
        const input = { ...values, method: values?.method?.value }

        isCreating ? await onCreate(input) : await onUpdate(input)

        onClose()
      } catch (error) {
        console.error(error)
        throw error
        // const errors = e.graphQLErrors.map(v => v)
        // setFieldError("server", errors)
      }
    }),
  }

  const title = isCreating ? "Create Payment" : "Update Payment"

  return (
    <Modal title={title} isOpen={isOpen} onClose={onClose}>
      <Formik {...formikProps}>
        {({ values }) => (
          <Form>
            <div className="py-3 flex justify-between">
              <div className="px-2 flex-1">
                <Fieldset
                  name="method"
                  label="Method *"
                  component="select"
                  options={PAYMENT_METHODS}
                />
              </div>

              <div className="px-2 flex-1">
                <Fieldset
                  name="amount"
                  label="Amount"
                  type="number"
                  placeholder="Enter amount"
                />
              </div>

              <div className="px-2 flex-1">
                <Fieldset name="timestamp" label="Date" type="date" />
              </div>
            </div>

            <nav className="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
                <button
                  type="submit"
                  className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                >
                  Save
                </button>
              </span>

              <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
                <button
                  onClick={onClose}
                  type="button"
                  className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                >
                  Cancel
                </button>
              </span>
            </nav>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

const TODAY = new Date().toISOString()
const TODAY_FORMATED = formatDate(TODAY, "yyyy-LL-dd")

const PaymentAssigmentFormModal = ({ isOpen, onClose, data, onSubmit }) => {
  const formikProps = {
    initialValues: {
      progress: [{ amount: 0, dueDate: TODAY_FORMATED }],
    },
    validateOnBlur: false,
    validateOnChange: false,
    // validationSchema: createTripSchema,
    onSubmit: useCallback(async (values, actions) => {
      try {
        await onSubmit(values)
        onClose()
      } catch (error) {
        console.error(error)
        throw error
        // const errors = e.graphQLErrors.map(v => v)
        // setFieldError("server", errors)
      }
    }),
  }

  return (
    <Modal title={"Payment schedule"} isOpen={isOpen} onClose={onClose}>
      <Formik {...formikProps}>
        {({ values }) => (
          <Form>
            <div className="px-2 py-3 mt-8">
              <FieldArray
                name="progress"
                render={(arrayHelpers) => (
                  <>
                    <header className="mt-2 flex justify-between items-center px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                      <div className="w-3/12">
                        <p>#</p>
                      </div>

                      <div className="w-3/12 ">
                        <p>Amount</p>
                      </div>

                      <div className="w-4/12">
                        <p>Due date</p>
                      </div>

                      <div className="w-1/12"></div>
                    </header>

                    {/* {JSON.stringify(data)} */}

                    <FieldDeposit
                      data={values?.progress}
                      arrayHelpers={arrayHelpers}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        arrayHelpers.push({
                          amount: 0,
                          dueDate: formatDate(new Date(), "yyyy-MM-dd"),
                        })
                      }
                      className="text-blue-500 hover:text-blue-700 py-2 px-1 my-6 rounded flex"
                    >
                      <svg
                        xmlns="http:www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>

                      <span className="ml-2">Add</span>
                    </button>
                  </>
                )}
              />

              <ErrorMessage name="payment.progress">
                {(msg) =>
                  typeof msg === "string" && (
                    <div className="text-sm error bg-red-200 text-red-800 p-2 mt-2">
                      {msg}
                    </div>
                  )
                }
              </ErrorMessage>
            </div>

            <nav className="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
                <button
                  type="submit"
                  className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                >
                  Save
                </button>
              </span>

              <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
                <button
                  onClick={onClose}
                  type="button"
                  className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                >
                  Cancel
                </button>
              </span>
            </nav>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

const FieldDeposit = ({ getIndex, data, endDateMax, arrayHelpers }) => {
  return (
    <div className="flex flex-col">
      {data?.map((part, index) => (
        <>
          <div
            key={index}
            className={`flex justify-between items-center my-4 px-6 space-x-2`}
          >
            <div className="w-3/12 text-base capitalize">
              <label>
                {index + 1}
                {/* {data.length === 1 || index !== data.length - 1
                  ? `${stringifyNumber(index + 1)} deposit`
                  : "Final payment"} */}
              </label>
            </div>

            <div className="w-3/12">
              <Fieldset name={`progress[${index}].amount`} type="number" />
            </div>

            <div className="w-4/12 flex justify-between items-center">
              <Fieldset
                name={`progress[${index}].dueDate`}
                type="date"
                min={TODAY}
                // max={endDateMax}
              />
            </div>

            <div className="w-1/12 flex justify-end items-center">
              <button
                type="button"
                className="inline-block"
                onClick={() => arrayHelpers.remove(index)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9 2C8.62123 2 8.27497 2.214 8.10557 2.55279L7.38197 4H4C3.44772 4 3 4.44772 3 5C3 5.55228 3.44772 6 4 6L4 16C4 17.1046 4.89543 18 6 18H14C15.1046 18 16 17.1046 16 16V6C16.5523 6 17 5.55228 17 5C17 4.44772 16.5523 4 16 4H12.618L11.8944 2.55279C11.725 2.214 11.3788 2 11 2H9ZM7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V8ZM12 7C11.4477 7 11 7.44772 11 8V14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14V8C13 7.44772 12.5523 7 12 7Z"
                    fill="#4A5568"
                  />
                </svg>
              </button>
            </div>
          </div>
        </>
      ))}
    </div>
  )
}

//Insurance tab

const Insurance = ({ data }) => {
  return (
    <div className="">
      {/* <legend className="w-full text-sm font-bold px-4 py-5 sm:px-6 text-gray-400 uppercase tracking-widest">
        About
      </legend> */}

      <dl>
        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
          <dt className="text-sm leading-5 font-medium text-gray-500">Value</dt>
          <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
            {data?.value}
          </dd>
        </div>

        {data?.value === "YES" && (
          <>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm leading-5 font-medium text-gray-500">
                Name
              </dt>
              <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
                {data?.quote?.products[`45ALPCA`].Title}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm leading-5 font-medium text-gray-500">
                Total
              </dt>
              <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
                {formatMoney(Number(data?.quote?.total))}
              </dd>
            </div>
          </>
        )}

        {/* <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
          <dt className="text-sm leading-5 font-medium text-gray-500">
            Destinations
          </dt>
          <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
            {data?.destinations?.map((_) => _?.name)?.join(", ")}
          </dd>
        </div> */}
      </dl>
    </div>
  )
}

const PASSENGERS = gql`
  query Trip($id: ID!) {
    trip(id: $id) {
      passengers {
        id
        relationship
        firstName
        lastName
        email
        phone
        gender
        birthdate

        street
        city
        state
        region
        zip
        country

        # ADDITIONAL
        passportNumber
        passportExparationDate

        weight
        height
        size

        emergencyContactPerson
        specialRequest

        roomateRequest
        smoker
        newsletter

        bookingId
        tripId
        userId

        user {
          id
          displayName
          email
        }
      }

      settings {
        passengerFields
      }
    }
  }
`

const CREATE_TRIP_PASSENGERS = gql`
  mutation CreateTripPassengers($input: CreateTripPassengersInput!) {
    createTripPassengers(input: $input) {
      id
    }
  }
`

const UPDATE_TRIP_PASSENGERS = gql`
  mutation UpdateTripPassengers($input: UpdateTripPassengersInput!) {
    updateTripPassengers(input: $input) {
      id
      relationship
      firstName
      lastName
      email
      phone
      gender
      birthdate

      street
      city
      state
      region
      zip
      country

      # ADDITIONAL
      passportNumber
      passportExparationDate

      weight
      height
      size

      emergencyContactPerson
      specialRequest

      roomateRequest
      smoker
      newsletter

      bookingId
      tripId
      userId

      user {
        id
        displayName
        email
      }
    }
  }
`

const DELETE_TRIP_PASSENGER = gql`
  mutation DeleteTripPassenger($input: DeleteTripPassengerInput!) {
    deleteTripPassenger(input: $input) {
      id
    }
  }
`

//PASSENGERS
const Passengers = ({
  data,
  passengerFields,
  numberOfLodgings,
  bookingId,
  tripId,
  userId,
  refetch,
}) => {
  const [createTripPassengers] = useMutation(CREATE_TRIP_PASSENGERS)
  const [updateTripPassengers] = useMutation(UPDATE_TRIP_PASSENGERS)
  const [deleteTripPassenger] = useMutation(DELETE_TRIP_PASSENGER)

  const handleCreate = async (passengers) => {
    const loadingId = toast.loading("Saving...")
    try {
      const input = { tripId, passengers }
      await createTripPassengers({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("Passenger created!")
      await refetch()
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleUpdate = async (passengers) => {
    const loadingId = toast.loading("Saving...")
    try {
      const input = { tripId, passengers }
      await updateTripPassengers({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("Passenger updated!")
      await refetch()
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this passenger?")) {
      const loadingId = toast.loading("Saving...")
      try {
        const input = { tripId, id }
        await deleteTripPassenger({ variables: { input } })
        toast.dismiss(loadingId)
        toast.success("Passenger deleted!")
        await refetch()
      } catch (error) {
        toast.dismiss(loadingId)
        toast.error("Could not save.")
        throw error
      }
    }
  }

  const [selectedPassenger, setSelectedPassenger] = useState(null)
  const [isEditing, setIsEditing] = useState(false)

  const openModal = useCallback(async () => {
    setIsEditing(true)
  }, [setIsEditing])

  const closeModal = useCallback(async () => {
    setSelectedPassenger(null)
    setIsEditing(false)
  }, [setIsEditing])

  const onSelect = (passenger) => {
    setSelectedPassenger(passenger)
    openModal()
  }

  const header = ["Name", "Relatioship", "Passport", "Special Request"]

  const allPassengersAdded = data?.length === numberOfLodgings

  return (
    <>
      <div style={{ maxHeight: "70vh", overflowY: "scroll" }}>
        <table className="min-w-full table-auto">
          <thead>
            <tr>
              {header.map((item, index) => (
                <th
                  key={index}
                  className="px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                  style={{ position: "sticky", top: "0" }}
                >
                  {item}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white">
            {data?.map((passenger, index) => (
              <tr
                className="table-row"
                key={index}
                onClick={() => onSelect(passenger)}
              >
                <td className="px-6 py-4 whitespace-no-wrap border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <Avatar
                        name={`${passenger?.firstName} ${passenger?.lastName}`}
                        // src={user.photoURL}
                        size="40"
                        round={true}
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm leading-5 font-medium text-gray-900">
                        {passenger?.firstName} {passenger?.lastName}
                      </div>
                      <div className="text-sm leading-5 text-gray-500">
                        {passenger?.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-no-wrap border-b border-gray-200">
                  <div className="text-sm leading-5 text-gray-500">
                    {passenger?.relationship}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500">
                  <div className="text-sm leading-5 text-gray-500">
                    {passenger?.passportNumber}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 font-medium">
                  <div className="text-sm leading-5 text-gray-500">
                    {passenger?.specialRequest}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* {hasMore && (
        <div ref={loaderRef} className="text-center bg-gray-100">
          Loading…
        </div>
      )} */}
      </div>

      {!allPassengersAdded && (
        <div className="w-full flex justify-center items-center my-6">
          <button
            onClick={openModal}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              className="-ml-1 mr-2 h-5 w-5 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              ariaHidden="true"
            >
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Create
          </button>
        </div>
      )}

      <PassengersForm
        isOpen={isEditing}
        onClose={closeModal}
        max={numberOfLodgings}
        bookingId={bookingId}
        tripId={tripId}
        userId={userId}
        fields={passengerFields}
        data={selectedPassenger ? [selectedPassenger] : []}
        onClose={closeModal}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </>
  )
}
